/**
 * Global toast / notification system.
 *
 * ----------------------------------------------------------------------------
 * Why
 * ---
 * The user's brief: "any and all errors that are there in the backend should
 * be displayed in the frontend as well, so that we can look into it."
 *
 * Per-page error banners (`<ErrorBox/>`) handle the cases where a page already
 * `await`s a request and `catch`-es the failure.  But the codebase is full of
 * places like:
 *
 *     SuperAdminAPI.users.list({...}).catch(() => null);
 *     ChatAPI.markRead(id).catch(() => {});
 *     fetchUsers().then(...).catch(() => setUsers([]));
 *
 * In those places the error is currently SWALLOWED — the user has no idea the
 * backend just 500'd.  Having a global toast layer means every axios failure
 * produces a visible message regardless of what the calling page does with
 * the error object.
 *
 * Architecture
 * ------------
 *   * `<NotificationProvider>` exposes context API + state.
 *   * `useNotify()`   - hook used inside React components.
 *   * `notify.error(...)` etc. - imperative facade callable from any module
 *                                (e.g. axios interceptors that don't have
 *                                access to React context). The provider
 *                                registers itself on this facade at mount.
 *   * `<Toaster />`   - renders the notifications stack.
 *
 * Toasts auto-dismiss after `DURATION_MS_*` unless they're errors, which
 * stick around until the user clicks them away (so backend bugs don't get
 * silently missed).
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

import { formatApiError } from './errors';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const DURATION_MS_INFO    = 4000;
const DURATION_MS_SUCCESS = 4000;
const DURATION_MS_WARNING = 6000;
// Errors are sticky — the user has to click ✕ to dismiss them so they
// can't be missed.  This is INTENTIONAL per the brief.
const DURATION_MS_ERROR   = 0;

// Cap the visible stack so a runaway interceptor can't fill the screen.
const MAX_VISIBLE = 6;

// ---------------------------------------------------------------------------
// Imperative facade — usable from non-React modules (e.g. axios interceptors)
// ---------------------------------------------------------------------------
/**
 * Module-scope handler installed by <NotificationProvider> on mount.
 * Until the provider mounts, calls are queued and replayed on registration
 * so the very first error (e.g. one fired during initial bootstrap) isn't
 * lost.
 */
let _push = null;
const _queue = [];

function _dispatch(payload) {
  if (_push) _push(payload);
  else _queue.push(payload);
}

/**
 * Public, framework-agnostic helper.  Use from anywhere:
 *
 *     import { notify } from './lib/notifications';
 *     notify.error('Something went wrong');
 *     notify.fromAxios(err);                // formats + pushes a toast
 */
export const notify = {
  info:    (message, opts = {}) => _dispatch({ ...opts, level: 'info',    message }),
  success: (message, opts = {}) => _dispatch({ ...opts, level: 'success', message }),
  warning: (message, opts = {}) => _dispatch({ ...opts, level: 'warning', message }),
  error:   (message, opts = {}) => _dispatch({ ...opts, level: 'error',   message }),
  /**
   * Convenience helper: takes an axios / generic error, formats it and
   * pushes an error toast.  Returns the same `error` so you can chain:
   *
   *     api.get(...).catch((e) => { notify.fromAxios(e); throw e; });
   */
  fromAxios: (error, opts = {}) => {
    _dispatch({
      ...opts,
      level: 'error',
      message: formatApiError(error),
      // Keep the original error reachable for diagnostic tooling.
      cause: error,
    });
    return error;
  },
};

// ---------------------------------------------------------------------------
// React context + provider
// ---------------------------------------------------------------------------
const NotificationContext = createContext(null);

let _nextId = 1;

export function NotificationProvider({ children }) {
  const [items, setItems] = useState([]);
  // Track timers so we can clear them on unmount / dismiss.
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setItems((curr) => curr.filter((n) => n.id !== id));
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback((payload) => {
    const id = _nextId++;
    const level = payload.level || 'info';
    const ttl = payload.duration ?? (
      level === 'error'   ? DURATION_MS_ERROR :
      level === 'warning' ? DURATION_MS_WARNING :
      level === 'success' ? DURATION_MS_SUCCESS :
                            DURATION_MS_INFO
    );

    const note = {
      id,
      level,
      message: payload.message ?? '',
      title:   payload.title,
      cause:   payload.cause,
      createdAt: Date.now(),
    };

    setItems((curr) => {
      const next = [...curr, note];
      // Drop oldest if over MAX_VISIBLE — but keep all errors that are still
      // unread when possible, since errors are the whole reason this exists.
      if (next.length > MAX_VISIBLE) {
        const overflow = next.length - MAX_VISIBLE;
        const trimmed  = next.slice(overflow);
        // Also clear timers for the dropped items.
        for (const dropped of next.slice(0, overflow)) {
          const t = timers.current.get(dropped.id);
          if (t) { clearTimeout(t); timers.current.delete(dropped.id); }
        }
        return trimmed;
      }
      return next;
    });

    if (ttl > 0) {
      const handle = setTimeout(() => dismiss(id), ttl);
      timers.current.set(id, handle);
    }

    return id;
  }, [dismiss]);

  // Register the imperative facade so non-React modules (axios interceptors)
  // can push toasts.  Replay anything that was queued before mount.
  useEffect(() => {
    _push = push;
    if (_queue.length) {
      const drain = _queue.splice(0, _queue.length);
      drain.forEach((p) => push(p));
    }
    return () => { _push = null; };
  }, [push]);

  // Cleanup all timers on unmount.
  useEffect(() => {
    return () => {
      for (const t of timers.current.values()) clearTimeout(t);
      timers.current.clear();
    };
  }, []);

  const clearAll = useCallback(() => {
    setItems([]);
    for (const t of timers.current.values()) clearTimeout(t);
    timers.current.clear();
  }, []);

  const value = useMemo(() => ({
    items, push, dismiss, clearAll,
    info:    (m, o) => push({ ...o, level: 'info',    message: m }),
    success: (m, o) => push({ ...o, level: 'success', message: m }),
    warning: (m, o) => push({ ...o, level: 'warning', message: m }),
    error:   (m, o) => push({ ...o, level: 'error',   message: m }),
    fromAxios: (e, o) => push({ ...o, level: 'error', message: formatApiError(e), cause: e }),
  }), [items, push, dismiss, clearAll]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotify() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotify must be used inside <NotificationProvider>');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// <Toaster /> — actual UI
// ---------------------------------------------------------------------------
const LEVEL_LABELS = {
  info:    'Info',
  success: 'Success',
  warning: 'Warning',
  error:   'Error',
};

const LEVEL_ICONS = {
  info:    'ℹ',
  success: '✓',
  warning: '⚠',
  error:   '⚠',
};

export function Toaster() {
  // The provider may not be mounted yet on the very first render in dev when
  // hot reload swaps modules — guard so we never throw out of <Toaster/>.
  const ctx = useContext(NotificationContext);
  if (!ctx) return null;
  const { items, dismiss } = ctx;
  if (!items || items.length === 0) return null;

  return (
    <div className="ty-toaster" role="region" aria-label="Notifications">
      {items.map((n) => (
        <div
          key={n.id}
          className={`ty-toast ty-toast--${n.level}`}
          role={n.level === 'error' ? 'alert' : 'status'}
        >
          <span className="ty-toast__icon" aria-hidden>{LEVEL_ICONS[n.level] || 'ℹ'}</span>
          <div className="ty-toast__body">
            <div className="ty-toast__title">{n.title || LEVEL_LABELS[n.level]}</div>
            <div className="ty-toast__msg">{n.message}</div>
          </div>
          <button
            type="button"
            className="ty-toast__close"
            aria-label="Dismiss"
            onClick={() => dismiss(n.id)}
          >×</button>
        </div>
      ))}
    </div>
  );
}

export default NotificationProvider;
